import { useState } from "react";
import { toast } from "sonner";
import { submitToHubSpot, HS_FORM_INQUIRY } from "@/lib/hubspot";

const INQUIRY_TYPES = [
  "Consulting Engagement",
  "Speaking / Keynote",
  "Training / Workshop",
  "Media / Interview",
  "Bulk Book Orders",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    company: "",
    inquiry_type: INQUIRY_TYPES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstname || !form.email || !form.message) {
      toast.error("First name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitToHubSpot({
        formId: HS_FORM_INQUIRY,
        pageName: "Contact — thedecisionmargin.com",
        fields: [
          { name: "firstname", value: form.firstname },
          { name: "lastname", value: form.lastname },
          { name: "email", value: form.email },
          { name: "company", value: form.company },
          { name: "inquiry_type", value: form.inquiry_type },
          { name: "message", value: form.message },
        ],
      });
      setSubmitted(true);
      toast.success("Inquiry sent. Dr. Avory's team will respond shortly.");
      setForm({
        firstname: "",
        lastname: "",
        email: "",
        company: "",
        inquiry_type: INQUIRY_TYPES[0],
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-t border-[#262626] bg-[#0a0a0a]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="overline overline-red mb-6">06 — Contact</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.95]">
            Begin a
            <br />
            <span className="text-[#d92d20]">conversation.</span>
          </h2>
          <p className="mt-8 text-white/70 leading-relaxed max-w-md">
            For consulting engagements, speaking inquiries, training programs, media
            requests, and bulk book orders — share the context and {"Dr. Avory's"} team
            will respond.
          </p>

          <div className="mt-12 space-y-px bg-[#262626] border border-[#262626] max-w-md">
            <ContactRow k="Email" v="info@kismetleads.com" href="mailto:info@kismetleads.com" />
            <ContactRow k="Publisher" v="Kismet Leads, LLC" />
            <ContactRow k="Based In" v="United States" />
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="border border-[#262626] p-10 lg:p-14"
            >
              <div className="overline overline-red mb-4">Received</div>
              <h3 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight">
                Your inquiry is in.
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed">
                Thank you. {"Dr. Avory's"} team will respond within 2 business days.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 border border-[#404040] hover:border-white text-white px-6 py-3 text-[12px] font-semibold tracking-[0.18em] uppercase"
              >
                Send another →
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-[#262626] p-8 lg:p-12 space-y-8"
            >
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="First name" required>
                  <input
                    data-testid="contact-input-firstname"
                    className="brut-input"
                    placeholder="First"
                    value={form.firstname}
                    onChange={set("firstname")}
                    required
                  />
                </Field>
                <Field label="Last name">
                  <input
                    data-testid="contact-input-lastname"
                    className="brut-input"
                    placeholder="Last"
                    value={form.lastname}
                    onChange={set("lastname")}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className="brut-input"
                    placeholder="you@organization.org"
                    value={form.email}
                    onChange={set("email")}
                    required
                  />
                </Field>
                <Field label="Organization">
                  <input
                    data-testid="contact-input-organization"
                    className="brut-input"
                    placeholder="Optional"
                    value={form.company}
                    onChange={set("company")}
                  />
                </Field>
                <Field label="Inquiry type" required>
                  <select
                    data-testid="contact-select-type"
                    className="brut-input appearance-none cursor-pointer"
                    value={form.inquiry_type}
                    onChange={set("inquiry_type")}
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#0a0a0a]">
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Message" required>
                <textarea
                  data-testid="contact-input-message"
                  className="brut-input"
                  placeholder="Briefly describe the engagement, audience, timing, and any specifics."
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  required
                />
              </Field>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-2">
                <p className="text-xs text-white/45 max-w-sm leading-relaxed">
                  Submissions are stored securely in Kismet Leads' CRM. We respond
                  within 2 business days.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-3 bg-[#d92d20] hover:bg-[#b91d13] disabled:opacity-60 text-white px-8 py-4 text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors w-full sm:w-auto"
                >
                  {submitting ? "Sending…" : "Send Inquiry →"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">
        {label} {required && <span className="text-[#d92d20]">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ k, v, href }) {
  const content = (
    <div className="bg-[#0a0a0a] p-5 flex items-center justify-between">
      <span className="overline">{k}</span>
      <span className="text-white text-sm font-medium">{v}</span>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:bg-[#101010] transition-colors">
      {content}
    </a>
  ) : (
    content
  );
}
